import { TypedDocumentNode, gql } from '@apollo/client';
import { useMemo, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import { FilterInDropdown } from '@sorare/core/src/atoms/dropdowns/FilterInDropdown';
import { RadioGroup } from '@sorare/core/src/atoms/inputs/RadioGroup';
import { BlockHeader } from '@sorare/core/src/atoms/layout/BlockHeader';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineS } from '@sorare/core/src/atoms/typography';
import { format as formatSeason } from '@sorare/core/src/lib/seasons';

import { SeasonStats } from 'pages/players/$slug/Overview/SeasonStats';

import { CareerStats_player } from './__generated__/index.graphql';

type Props = {
  player: CareerStats_player;
};

const messages = defineMessages({
  season: { id: 'CareerStats.season', defaultMessage: 'Season {season}' },
  noSeason: {
    id: 'CarrerStats.noSeason',
    defaultMessage: 'No season stats available',
  },
});

interface CareerSeasonSelectorProps {
  startYear: number;
  setStartYear: (value: number) => void;
  player: CareerStats_player;
}

const CareerSeasonSelector = ({
  startYear,
  setStartYear,
  player,
}: CareerSeasonSelectorProps) => {
  const options = useMemo(
    () =>
      player.careerSeasons.map(s => ({
        label: formatSeason(s.startYear),
        value: s.startYear,
      })),
    [player.careerSeasons]
  );

  const selectedOption = useMemo(
    () => options.find(o => o.value === startYear),
    [options, startYear]
  );
  const { value: selectedValue } = selectedOption || {};

  return (
    <SBHorizontal gap={0}>
      <Vertical gap={1.5}>
        <HeadlineS as="h2">
          <FormattedMessage
            id="CareerStats.playerStats"
            defaultMessage="{player} stats"
            values={{ player: player.displayName }}
          />
        </HeadlineS>
        <FilterInDropdown
          buttonLabel={selectedValue && formatSeason(selectedValue)}
        >
          {({ closeDropdown }) => (
            <RadioGroup
              options={options.map(option => ({
                ...option,
                value: option.value.toString(),
              }))}
              initiallySelectedValue={selectedValue?.toString()}
              name="player-price-graph-rarity"
              onChange={value => {
                setStartYear(+value);
                closeDropdown();
              }}
            />
          )}
        </FilterInDropdown>
      </Vertical>
    </SBHorizontal>
  );
};

const StyledVertical = styled(Vertical).attrs({ gap: 0 })`
  align-items: flex-start;
`;

const NoSeason = () => {
  const { formatMessage } = useIntl();

  return (
    <StyledVertical>
      <BlockHeader title={formatMessage(messages.noSeason)} />
    </StyledVertical>
  );
};

export const CareerStats = ({ player }: Props) => {
  const [startYear, setStartYear] = useState(
    () => player.careerSeasons.slice(-1)[0]?.startYear
  );

  if (!startYear) {
    return <NoSeason />;
  }

  return (
    <FullWidthContainer>
      <Vertical gap={1.5}>
        <CareerSeasonSelector
          startYear={startYear}
          setStartYear={setStartYear}
          player={player}
        />
        <SeasonStats seasonStartYear={startYear} playerSlug={player.slug} />
      </Vertical>
    </FullWidthContainer>
  );
};

CareerStats.fragments = {
  player: gql`
    fragment CareerStats_player on Player {
      slug
      displayName
      careerSeasons(type: CLUB) {
        startYear
      }
    }
  ` as TypedDocumentNode<CareerStats_player>,
};
